'use client';

import React, { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useLoginModal } from '@/components/auth/login';
import Link from 'next/link';
import { useMarketplace } from '@/hooks/useMarketplace';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { Search, ShoppingCart, User, Menu, ChevronLeft, MapPin, Package, Utensils } from 'lucide-react';
import { useCartStore, CartItem } from '@/store/cart-store';
import { useUserStore } from '@/store/user-store';
import { useAddressStore } from '@/store/address-store';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { SimpleAddressBar } from '@/components/ui/simple-address-bar';
import { storageService } from "@/services/storage.service"
import { API } from '@/constants/api';
import { toast } from 'sonner';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isOpen, openLoginModal, closeLoginModal, LoginModal } = useLoginModal();
  const pathname = usePathname();
  const router = useRouter();
  
  // Access stores
  const cartStore = useCartStore();
  const userStore = useUserStore();
  const addressStore = useAddressStore();
  
  // Get Next-Auth authentication state
  const { status, session, accessToken, signOut, update } = useAuth();
  
  // Combined authentication state from both auth systems
  const nextAuthAuthenticated = status === 'authenticated';
  const zustandAuthenticated = userStore.isAuthenticated;
  let isAuthenticated = nextAuthAuthenticated || zustandAuthenticated;
  const { tcpParams } = useMarketplace();
  const isTcpApp = (tcpParams?.source || '').toLowerCase() === 'tcp';
  // User data (prioritize next-auth if available)
  const user = nextAuthAuthenticated 
    ? { name: session?.user?.name || 'User', email: session?.user?.email || '' } 
    : userStore.user;
  
  // Handle logout
  const handleLogout = async () => {
    try {
      console.log('[Header] Logging out user');
      await storageService.removeItem(`marketplace-vendor-login-data-${API.MARKETPLACE_REFERENCE_ID}`);
      localStorage.removeItem('app_access_token');
      // Sign out from Next-Auth
      // if (nextAuthAuthenticated) {
        console.log('[Header] Signing out from NextAuth');
        await signOut({ redirect: false });
        await update();
      // }
      
      // Sign out from zustand (existing auth)
      // if (zustandAuthenticated) {
        console.log('[Header] Signing out from Zustand store');
        // userStore.logout();
      // }
      userStore.logout();
      window.location.reload();
      window.location.href='/';
      cartStore.getState().clearCart();
      // Redirect to home page after logout
      //router.push('/');      
      // Close the menu after logout
      setIsMenuOpen(false);
    } catch (error) {
      console.error('[Header] Error during logout:', error);
      // Fallback logout - redirect to home
      window.location.href = '/';
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add state for cart count
  const [cartCount, setCartCount] = useState(0);

  // Update cart count calculation and add subscription
  useEffect(() => {
    // Initial count
    const updateCartCount = () => {
      const items = cartStore.getState()?.items || [];
      const count = items.reduce((total: any, item: { quantity: any; }) => total + (item?.quantity || 0), 0);
      setCartCount(count);
    };

    // Set initial count
    updateCartCount();

    // Subscribe to changes
    const unsubscribe = cartStore.subscribe((state: { items: never[]; }) => {
      const items = state?.items || [];
      const count = items.reduce((total: any, item: { quantity: any; }) => total + (item?.quantity || 0), 0);
      setCartCount(count);
    });

    return () => unsubscribe?.();
  }, [cartStore]);

  // Handle search click - navigate to search page
  const handleSearchClick = () => {
    router.push('/search');
  };

  // Add this function near other handler functions
  const handleCartClick = () => {
    const items = cartStore.getState()?.items || [];
    if (items.length === 0) return router.push('/cart');

    const firstItem = items[0];
    const minOrder = firstItem?.restaurant_info?.merchantMinimumOrder;
    const totalAmount = items.reduce((sum: number, item: CartItem) => sum + (item.price * item.quantity), 0);

    if (minOrder && totalAmount < minOrder) {
      toast.error(
        <div className="flex flex-col gap-1">
          <p className="font-medium">Minimum order amount not met</p>
          <p className="text-sm text-gray-500">
            Add ₹{(minOrder - totalAmount).toFixed(2)} more to reach minimum order amount of ₹{minOrder}
          </p>
        </div>
      );
      return;
    }

    router.push('/cart');
  };

  // Check if it's the home page
  const isHomePage = pathname === '/';
  const isSearchPage = pathname === '/search';
  const isDetailPage = pathname?.includes('/restaurants/') && pathname.split('/').length > 3;

  return (
    <>
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isDetailPage || isScrolled ? 'bg-white shadow-sm' : 'bg-white'
      }`}
    >
      <div className="container mx-auto px-4 py-4 md:py-5">
        <div className="flex items-center">
          {/* Back button - Hidden on Home Page */}
          {!isHomePage && (
            <button 
              onClick={() => router.back()} 
              className="mr-2 p-1 rounded-full hover:bg-gray-100 text-gray-700"
              aria-label="Go back"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          
          {/* Logo */}
          <Link href="/" className="relative h-10 w-28 md:h-12 md:w-36 flex-shrink-0">
            <Image
              src="/logo.png"
              alt="Qmin"
              fill
              className="object-contain"
              priority
            />
          </Link>

          {/* Address Bar - Only show on non-home pages */}
          {!isHomePage && (
            <div className="flex-1 mx-4 max-w-lg hidden md:block">
              <SimpleAddressBar />
            </div>
          )}
          
          {/* Mobile Address - Only show on non-home pages */}
          {!isHomePage && (
            <div className="md:hidden flex-1 overflow-hidden">
              <div className="flex items-center ml-2">
                <MapPin className="h-5 w-5 text-gray-500 flex-shrink-0" />
                <div className="ml-1 truncate text-sm text-gray-700">
                  {addressStore.address ? (
                    <button 
                      onClick={handleSearchClick}
                      className="truncate"
                    >
                      {addressStore.address}
                    </button>
                  ) : (
                    <button 
                      onClick={handleSearchClick}
                      className="text-gray-500"
                    >
                      Enter delivery location
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Right side icons */}
          <div className="flex items-center ml-auto space-x-2 md:space-x-3">
            {/* Search Icon */}
            {!isSearchPage && isAuthenticated && (
              <button 
                onClick={handleSearchClick}
                className="p-2 text-gray-700 hover:bg-gray-100 rounded-full"
                aria-label="Search"
              >
                <Search className="h-5 w-5" />
              </button>
            )}
            
            {/* Cart Button */}
            { isAuthenticated && ( 
              <Button variant="ghost" size="icon" className="relative p-2 text-gray-700 hover:bg-gray-100 rounded-full"
              onClick={handleCartClick}>
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <motion.span 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-[#900f21] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
                  >
                    {cartCount}
                  </motion.span>
                )}
              </Button>
            )}

            {/* User Profile Button - Only visible on desktop and when authenticated */}
            {isAuthenticated && (
              <div className="hidden md:block">
                <Link href="/profile">
                  <div className="h-9 w-9 bg-[#900f21] text-white rounded-full flex items-center justify-center text-base font-medium cursor-pointer hover:bg-[#8a6d3b]/90 transition-colors">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                </Link>
              </div>
            )}
            {!isAuthenticated && (
              <div className="hidden md:block">
              <div
                className="h-9 w-9 bg-[#900f21] text-white rounded-full flex items-center justify-center text-base font-medium cursor-pointer hover:bg-[#8a6d3b]/90 transition-colors"
                onClick={openLoginModal}
                role="button"
                aria-label="Go to profile"
              >
                <User size={18} />
              </div>
            </div>
            )}

            {/* Mobile Menu Trigger */}
            {isAuthenticated && (
              <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="p-2 text-gray-700 hover:bg-gray-100 rounded-full">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[90%] max-w-[350px] p-0 overflow-y-auto">
                <div className="flex flex-col h-full bg-white z-[2147483647]">
                    {/* Mobile Menu Header */}
                    {/* <div className="flex items-center justify-between px-4 pt-6 pb-4 border-b border-gray-100">
                      <h2 className="text-xl font-bold text-[#900f21]">Menu</h2>
                    </div> */}

                    {/* User Account Options */}
                    <div className="px-4 py-6 border-b border-gray-100">
                      {isAuthenticated ? (
                        <div className="flex flex-col space-y-4">
                          <div className="flex items-center">
                            <div className="h-12 w-12 bg-[#900f21] text-white rounded-full flex items-center justify-center text-xl font-bold mr-3">
                              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <div className="font-medium">{user?.name || 'User'}</div>
                              <div className="text-sm text-gray-500">{user?.email || ''}</div>
                            </div>
                          </div>
                          {!isTcpApp && (
                          <SheetClose asChild>
                            <Link
                              href={'/profile'} 
                              onClick={(e) => {
                                e.stopPropagation();
                                router.push('/profile');
                                
                              }}
                              prefetch={true}
                              className="text-[#900f21] font-medium"
                            >
                              View Profile
                            </Link>
                          </SheetClose>
                        )}
                        </div>
                      ) : (
                        <SheetClose asChild>
                          <Button 
                            onClick={openLoginModal} 
                            className="bg-[#900f21] hover:bg-[#900f21]/90 text-white w-full rounded-md py-2.5 font-medium"
                          >
                            Login
                          </Button>
                        </SheetClose>
                      )}
                    </div>

                    {/* Menu Options */}
                    {isAuthenticated ? 
                    (<nav className="flex flex-col px-4 py-2">
                      {/* <SheetClose asChild>
                        <Link
                          href="/profile"
                          className="flex items-center space-x-3 text-gray-900 hover:text-[#900f21] font-medium text-lg py-3 border-b border-gray-100"
                          onClick={() => {
                            setIsMenuOpen(false);
                          }}
                          prefetch={true}
                        >
                          <User className="h-5 w-5" />
                          <span>Profile</span>
                        </Link>
                      </SheetClose> */}
                      <SheetClose asChild>
                        <Link
                          href="/restaurants"
                          className="flex items-center space-x-3 text-gray-900 hover:text-[#900f21] font-medium text-lg py-3 border-b border-gray-100"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <Utensils className="h-5 w-5" />
                          <span>Restaurants</span>
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link
                          href="/orders"
                          className="flex items-center space-x-3 text-gray-900 hover:text-[#900f21] font-medium text-lg py-3 border-b border-gray-100"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <Package className="h-5 w-5" />
                          <span>Orders</span>
                        </Link>
                      </SheetClose>
                    </nav>): ''}

                    {/* Logout Button (Only shown if authenticated) */}
                    {isAuthenticated && !isTcpApp && (
                      <div className="mt-auto p-4 border-t border-gray-100">
                      {!isTcpApp && (
                        <SheetClose asChild>
                          <Button 
                            variant="outline" 
                            className="w-full justify-center rounded-md text-red-600 border-red-200 hover:bg-red-50"
                            onClick={handleLogout}
                          >
                            Logout
                          </Button>
                        </SheetClose>
                       )}
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </div>
    </header>
    <LoginModal isOpen={isOpen} onClose={closeLoginModal} />
    </>
  );
};

// Default export for the Header component
export { Header as default };
